/**
 * 实时提示条的**文案唯一来源**
 *
 * 本项目一贯做法（`theme-labels.ts` / `reminder-labels.ts` / `sovereignty-labels.ts` / …）：
 * 文案与逻辑分开，让 **spec 能对"字"断言**（禁用 `includes('提示')` 这类恒真信号）。
 *
 * ## 🔴 文案的来源与"哪些是原型原文、哪些是新增"
 *
 * | 文案 | 来源 |
 * |---|---|
 * | 「新增 N 笔 · 查看」 | L 域 PRD §5.2 口径 5 / 架构 §7.2 / `DR-018` —— **逐字**（原型原文） |
 * | 「该流水已被删除」 | L 域 PRD §5.2 口径 6（删除优先）· `AC-L2.6` —— **逐字** |
 * | 其余动作句 | **本批新增** —— PRD 只给了"新增 N 笔"一例，其余动作（改/删/恢复/转账）的措辞由本批定下 |
 *
 * ## ⚠️ 「N 笔」怎么来的（🔴 端侧**只搬运**，不聚合）
 *
 * 架构 §7.2 写的是「**新增 N 笔**」。契约明文：**WS 载荷不携带业务字段**（`contract/realtime.ts`
 * 文件头）⇒ 端侧**收不到**"这批新增了几笔"。
 * ⇒ N 只能来自**端侧把连续到达的同类事件合并计数**（`features/realtime-toast.ts` 的
 * `mergeToast`）—— 那是**事件计数**，不是"交易笔数聚合"（前者是传输层事实，后者才是被禁的口径）。
 *
 * 🔴 因此"新增 N 笔"里的 N **在只有一条事件时就是 1** —— 文案模板统一写 `1`，
 * 合并时由 `countedText()` 替换（见 `realtime-toast.ts`）。
 */

/** 提示条上「查看」按钮（架构 §7.2 原文「新增 N 笔 · **查看**」） */
export const TOAST_VIEW = '查看';

/** 提示条的 `aria-label`（可访问性 —— 提示条是 `role="status"`，读屏要能念出它是干什么的） */
export const TOAST_ARIA = '协作动态';

/**
 * 提示条末尾的引导语 —— 🔴 **不写"点击查看"**（原型原文是「· 查看」两个字的按钮）。
 * 这条常量给 `aria-describedby` 用，说明"查看"会做什么。
 */
export const TOAST_VIEW_HINT = '查看刚被修改的内容';

/**
 * 「在流水页」场景的文案（`AC-L2.8` 另一半）—— ⚠️ **本批未使用**（见 `realtime-toast.ts` 文件头）。
 *
 * 放在这里而不是等实现时再写，是因为**它属于同一批文案**：将来做"静默插入 + 高亮 3 秒"时
 * 直接从本文件取，不必再想措辞、也不必在组件里写字面量。
 * ⚠️ 它目前**没有任何引用** —— `lint` 与 `check:arch` **都不检查"零引用导出"**（实测确认），
 * 故不会让门禁变红；仍**如实登记**在本批 changelog，避免下次有人 grep 到这个常量却
 * 以为该能力已实现。
 */
export const TOAST_INLINE_FLASH = '已插入并高亮 3 秒';
